import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { SubscriptionStatus } from '@cf-shared/data-model/couch-user';
import { sleep } from '@cf-shared/shared-util';
import { AuthService } from './auth.service';
import { CouchLoginReason } from './couch-login-storm';
import { CloudFunctionsService } from './cloud-functions.service';
import { CheckoutResult, UserSubscriptionResult_CheckoutRedirect } from '@cf-shared/user-subscription-interface';
import { LoadingController } from 'providers/loading-controller';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { TelemetryService } from './telemetry.service';
import { LoggingService } from './logging.service';
import { errorReportFields } from './error-patterns';
import { UrlQueryService } from './url-query-service';
import { getRootDomain, isCapacitorNative } from 'environments/environment-helpers';
import { NavController } from 'providers/nav-controller';
import { IPurchaseSuccessPage } from 'pages/purchase-success/purchase-success-interface';
import { IHomePage } from 'pages/home/home-interface';
import { ISubscribePage } from 'pages/subscribe/subscribe-interface';

// Discriminated-union result of assureCheckoutResultHandledAsync(). When the
// caller (app.component.ts) sees `informMagicLink`, it routes the user to
// the login subscription-success phase; the install URLs ride alongside so
// that phase can render install badges.
export type CheckoutHandledResult =
  | { action: 'reload' }
  | { action: 'informMagicLink', androidInstallUrl?: string, iosInstallUrl?: string }
  | undefined;

export enum SignInTokenError {
  Expired = 'expired',
  Network = 'network',
  Other = 'other',
}

@Injectable()
export class ExternalFlowService {
  private signInToken: string = undefined;
  private claimToken: string = undefined;
  private signInTokenError: SignInTokenError = undefined;
  private pendingAccountSwitch: { signInToken: string; claimToken?: string; tokenEmail: string; currentEmail: string } = undefined;
  private checkoutResult: CheckoutResult = undefined;
  private lastCheckoutError: string = undefined;

  constructor(
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private loading: LoadingController,
    private alertCtrl: AlertController,
    private telemetry: TelemetryService,
    private urlQueryService: UrlQueryService,
    private navCtrl: NavController,
    private logging: LoggingService,
  ) {
    this.parseExternalFlowParams();
  }

  /**
   * Write a cold-start link's path and query into the address bar, and read the
   * boot parameters out of it, WITHOUT reloading the app.
   *
   * `pathAndQuery` is a path on our own site, never a whole address: the link
   * names the public site and this web view serves from a local address of its
   * own, so writing the whole thing would move the app off its own origin.
   *
   * Everything refreshed here is boot-time state that nothing has read yet, and
   * this runs before the auth init for that reason. Why the reload it replaces
   * was a problem, and what depends on the ordering, are in
   * design-docs/purchase-deep-linking.md, "A cold-start link is read in place".
   */
  adoptBootParamsInPlace(pathAndQuery: string) {
    // this is the same address-bar write the app already uses to strip parameters
    // out once they are used, so it is proven on both phone platforms
    history.replaceState({}, '', pathAndQuery);
    this.urlQueryService.rereadAddressBar();
    // the telemetry service keeps its OWN copy of the tracking data, taken when it
    // was built, so refreshing the URL service does not reach it
    this.telemetry.refreshTrackingDataFromUrl();
    this.parseExternalFlowParams();
  }

  parseExternalFlowParams() {
    // handle sign-in token and claim token
    this.signInToken = this.urlQueryService.getUrlParam('signInToken');
    this.claimToken = this.urlQueryService.getUrlParam('claimToken');

    // handle checkout result
    let checkoutResult = this.urlQueryService.getUrlParam('checkout_result');

    // if PayPro redirected us to the wrong hostname (e.g. dev.mh-physique.app
    // instead of <tag>.dev.mh-physique.app, or www instead of canary), redirect
    // back to the hostname that initiated checkout
    if (checkoutResult) {
      const returnHost = document.cookie.match(/(?:^|;\s*)ppg_return_host=([^;]+)/)?.[1];
      if (returnHost && returnHost !== location.host) {
        // clear the cookie
        document.cookie = `ppg_return_host=;domain=.${getRootDomain()};path=/;max-age=0;secure;samesite=lax`;
        // redirect, preserving path and query params
        const url = new URL(location.href);
        url.host = returnHost;
        console.log(`ExternalFlow: PayPro returned to wrong host, redirecting to ${url.host}`);
        location.replace(url.toString());
        return;
      }
      // clear the cookie (no longer needed)
      document.cookie = `ppg_return_host=;domain=.${getRootDomain()};path=/;max-age=0;secure;samesite=lax`;
    }

    if (!checkoutResult) {
      // nothing to do
    } else if (
      checkoutResult == CheckoutResult.Fulfilled ||
      checkoutResult == CheckoutResult.Declined ||
      checkoutResult == CheckoutResult.Review ||
      checkoutResult == CheckoutResult.Waiting) {
      this.checkoutResult = checkoutResult as CheckoutResult;
      console.log(`ExternalFlow: Checkout result "${checkoutResult}"`);
    } else {
      checkoutResult = undefined;
      console.error(`ExternalFlow: Invalid checkout result "${checkoutResult}"`);
    }
  }

  trackPaymentError(checkoutResult: CheckoutResult) {
    this.telemetry.trackEvent('payment_error', {
      checkout_result: checkoutResult,
      url_params: this.urlQueryService.getAllUrlParamsAsObject()
    });
  }

  async assureCheckoutResultHandledAsync(): Promise<CheckoutHandledResult> {
    // nothing to do if we don't have a checkout result
    if (!this.checkoutResult) {
      return undefined;
    }
    console.log('App: performing post-purchase redirection...');

    let checkoutResult = this.checkoutResult; // store for later, as we will clear it
    let redirectResult = await this.cloudFunctions.userSubscriptionAsync({
      action: 'CheckoutRedirect',
      checkoutResult: checkoutResult,
      checkoutId: this.urlQueryService.getUrlParam('x-checkoutid'),
    }) as UserSubscriptionResult_CheckoutRedirect;
    this.checkoutResult = undefined;
    this.urlQueryService.clearUrlSearchKeys(['checkout_result']);
    this.urlQueryService.clearUrlSearchKeys(['x-checkoutid']);

    // extract install URLs once — both happy-path (processCheckoutSuccessAsync) and
    // informMagicLink consumers read from this single extraction so they can't drift apart
    const androidInstallUrl = redirectResult.androidInstallUrl;
    const iosInstallUrl = redirectResult.iosInstallUrl;

    // if the result contains a sign-in token, we need to sign in the user
    if (redirectResult.token) {
      // if in webview
      if (this.auth.isWebView()) {
        // instruct the user to use the link from the email
        return { action: 'informMagicLink', androidInstallUrl, iosInstallUrl };
        // if ordinary browser
      } else {
        try {
          await this.auth.signInWithTokenAsync(redirectResult.token);
          console.log(`ExternalFlow: checkout login succeeded`);
          // and reload to make sure the user is in the correct state
          return { action: 'reload' };
        } catch (error) {
          // the person still gets the "use the link in your email" message below, but they paid
          // and were not signed in, so this has to reach a debugger (#5191)
          this.logging.trackSilentProblem('CheckoutTokenSignInFailed', errorReportFields(error));
          return { action: 'informMagicLink', androidInstallUrl, iosInstallUrl };
        }
      }
      // if no token, means we are already signed in - or payment declined
    } else {
      // if the checkout was successful, we need to process it
      if (checkoutResult == CheckoutResult.Fulfilled || checkoutResult == CheckoutResult.Review) {
        console.log(`ExternalFlow: Checkout fulfilled`);
        await this.processCheckoutSuccessAsync(androidInstallUrl, iosInstallUrl);
        return undefined;
      } else if (checkoutResult == CheckoutResult.Waiting) {
        this.lastCheckoutError = LL.subscribe.errors.checkoutAwaitingProcessing();
        return undefined;
      } else if (checkoutResult == CheckoutResult.Declined) {
        console.log(`ExternalFlow: Checkout declined`);
        this.lastCheckoutError = LL.subscribe.errors.checkoutDeclined();
        this.trackPaymentError(checkoutResult);
        return undefined;
      } else {
        console.error(`ExternalFlow: Checkout unknown: "${checkoutResult}"`);
      }
    }

    return undefined;
  }

  // After the checkout is done, PayPro has to call the Firebase webHook, which then needs to write this info into CouchDb.
  // We need to keep querying the couchdb in case it takes some time.
  async waitSubscriptionRegisteredAsync(): Promise<boolean> {
    // Once PayPro's IPN reaches our webhook, the user is promoted to
    // SubscriptionStatus.Temporary → AuthRole.Paid almost immediately. The
    // slow and variable part is PayPro itself sending that IPN: usually a
    // second or two, but the tail can reach ~20s (measured over a week of real
    // signups). So we poll the login until the paid role shows up.
    //
    // The wait happens at the START of each step, so a poll ALWAYS runs after
    // the longest wait. An earlier version polled first and slept at the end,
    // which left the final sleep window unpolled — an IPN that landed in it was
    // missed even though the paid role was already written to CouchDB, and the
    // loop bailed seconds after the role became available. The last poll here
    // lands at ~36s, which comfortably covers the observed worst case.
    const retryDelaysMs = [0, 1000, 2000, 3000, 5000, 10000, 15000];
    for (const delayMs of retryDelaysMs) {
      if (delayMs > 0) {
        await sleep(delayMs);
      }
      const loginSuccess = await this.auth.attemptCouchLoginAsync(CouchLoginReason.PayProPaidPoll);
      if (loginSuccess && this.auth.getAuthRole() >= AuthRole.Paid) {
        console.log('PayPro: Subscription registration successful.');
        return true;
      }
      console.log('PayPro: User payment still not processed. Retrying. ');
    }
    console.error('PayPro: Bailing subscription wait loop!');
    return false;
  }

  async processCheckoutSuccessAsync(androidInstallUrl?: string, iosInstallUrl?: string) {
    // if already paid
    if (this.auth.getAuthRole() >= AuthRole.Paid) {
      console.log('ExternalFlow: Checkout already registered, nothing to do.');
      return;
    }

    let loader = this.loading.create({ content: LL.common.loading.registeringPayment() });
    await loader.present();
    try {
      // wait a bit to give time to UI to show the loadingCtrl, so that the refresh doesn't look weird
      await Promise.all([this.waitSubscriptionRegisteredAsync(), sleep(1000)]);
    } finally {
      // dismiss in finally so the spinner can't get stuck if the wait throws
      await loader.dismiss();
    }

    // if subscription still not registered after waiting
    if (this.auth.getAuthRole() < AuthRole.Paid) {
      console.error('PayPro: failed waiting for subscription registration');
      this.telemetry.trackEvent('payment_registration_timeout', {});

      // Two regimes can land here with Temporary status set:
      // (a) Phase 1 (#3638) live — Temporary grants AuthRole.Paid on the IPN, so
      //     normally the polling loop returned at the AuthRole >= Paid check
      //     above. Reaching this branch implies the CouchAuth refresh hasn't
      //     yet picked up the new role; routing to home is correct because
      //     server-side authoritative state already grants access.
      // (b) Phase 1 not yet shipped — Temporary doesn't grant Paid. Routing to
      //     home is still preferable to the old reloadAppAsync that bounced
      //     through login → subscribe (the failed-payment-looking UX from Bug
      //     5); home will render its own role-gated content. The PRD's
      //     Prerequisites section explicitly accepts this degradation as no
      //     worse than today's state.
      // Either way, no alert and no reload. The Manage Subscription page's
      // Temporary info card (R5) is the user-facing affordance.
      if (this.auth.getSubscriptionStatus() === SubscriptionStatus.Temporary) {
        console.log('PayPro: Temporary status set — routing to home (no alert).');
        await this.navCtrl.setRootAsync(IHomePage, {});
        return;
      }

      // Otherwise (typically Fulfilled but PPG webhook hasn't landed yet): show
      // a milder "we're confirming" alert, not the old "Retry" alert. After OK,
      // route to home if the user has any Paid-tier role (covers a late Temporary
      // flip) or back to subscribe if still free. Crucially: no reloadAppAsync —
      // the old reload bounced the user through login → subscribe, which read
      // as a failed payment.
      await this.alertCtrl.showAlertAsync({
        title: LL.subscribe.alerts.paymentBeingConfirmed.title(),
        message: LL.subscribe.alerts.paymentBeingConfirmed.message(),
        buttons: [{
          text: LL.common.buttons.ok(),
          role: AlertButtonRole.Primary,
          result: null
        }]
      });
      // The alert dwells for user-controlled time; the IPN may land during
      // that window. Refresh auth once so the routing decision below uses
      // current state rather than the stale snapshot from polling-exhausted.
      // Best-effort: a refresh failure shouldn't block the routing fall-through.
      try {
        await this.auth.attemptCouchLoginAsync(CouchLoginReason.PayProAfterAlert);
      } catch (refreshErr) {
        // fall through to the role check, which routes on whatever role the loop
        // captured. attemptCouchLoginAsync reports the failures it handles itself, but
        // those RETURN false — they do not throw. Anything thrown out of it is
        // unexpected, so it is reported here (#5191).
        this.logging.trackSilentProblem('PayProPostAlertAuthRefreshFailed', errorReportFields(refreshErr));
      }
      if (this.auth.getAuthRole() >= AuthRole.Paid) {
        await this.navCtrl.setRootAsync(IHomePage, {});
      } else {
        await this.navCtrl.setRootAsync(ISubscribePage, {});
      }
    } else {
      console.log('PayPro: Subscription registration successful.');
      // on web/PWA happy path, push a modal install-affordance page. The function
      // also early-returns near the top when the user is already paid (re-entry on
      // success URL via back-button / refresh) — that branch deliberately skips
      // the modal to avoid showing it on every reload. Native happy path also
      // skips (user is already on the device with the app installed). And we
      // skip if both URLs are absent (legacy callers or env where the server
      // didn't supply them).
      if (!isCapacitorNative() && (androidInstallUrl || iosInstallUrl)) {
        await this.navCtrl.pushAsync(IPurchaseSuccessPage, {
          androidInstallUrl,
          iosInstallUrl
        });
      }
    }
  }

  getLastCheckoutError(): string | undefined {
    return this.lastCheckoutError;
  }

  getSignInToken(): string {
    return this.signInToken;
  }

  consumeSignInToken() {
    this.urlQueryService.clearUrlSearchKeys(['signInToken', 'claimToken']);
    this.signInToken = undefined;
    this.claimToken = undefined;
  }

  getClaimToken(): string {
    return this.claimToken;
  }

  hasExternalFlowPending(): boolean {
    return !!this.signInToken || !!this.claimToken || !!this.checkoutResult;
  }

  setPendingAccountSwitch(data: { signInToken: string; claimToken?: string; tokenEmail: string; currentEmail: string }) {
    this.pendingAccountSwitch = data;
  }

  consumePendingAccountSwitch() {
    const data = this.pendingAccountSwitch;
    this.pendingAccountSwitch = undefined;
    return data;
  }

  setSignInTokenError(error: SignInTokenError) {
    this.signInTokenError = error;
  }

  consumeSignInTokenError(): SignInTokenError | undefined {
    let error = this.signInTokenError;
    this.signInTokenError = undefined;
    return error;
  }
}
